/**
 * A measurement unit is invalid if it's blank (after trimming) or a
 * case-insensitive duplicate of another unit on the same field — the
 * client-side mirror of the backend's per-field unit constraint.
 */
export function isMeasurementUnitInvalid(
  units: string[],
  index: number
): boolean {
  const trimmed = units.map(unit => unit.trim());
  const value = trimmed[index];
  if (value === '') return true;
  const lower = value.toLowerCase();
  return trimmed.some(
    (other, otherIndex) => otherIndex !== index && other.toLowerCase() === lower
  );
}

/**
 * Whether any *existing* unit entry is blank or a duplicate. Deliberately
 * does not flag a zero-length array — used to gate "Add Unit" actions,
 * where an empty list is the normal starting state a user must be able to
 * add to, not itself an entry-level error.
 */
export function hasInvalidMeasurementUnits(units: string[]): boolean {
  return units.some((_, index) => isMeasurementUnitInvalid(units, index));
}

/**
 * Whether a measurement field's units are invalid for save purposes —
 * either an entry-level problem (blank/duplicate) or the field having zero
 * units configured at all, mirroring the backend's
 * `MeasurementFieldValidator` `Count == 0` rule ("Measurement fields
 * require at least one unit.").
 */
export function isMeasurementFieldInvalidForSave(units: string[]): boolean {
  return units.length === 0 || hasInvalidMeasurementUnits(units);
}
